import type {
  PetType,
  AiConfig,
  SearchResponse,
  SummarizeResponse,
  PdfResponse,
  PdfOptions,
  SymptomSummary,
  ErrorResponse,
} from './types'

const API_BASE = import.meta.env.PROD
  ? 'https://vet-symptom-api.sayasaya.workers.dev'
  : '/api'

/**
 * APIエラー
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * APIリクエストのラッパー
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  })

  const data = await response.json()

  if (!response.ok) {
    const error = data as ErrorResponse
    throw new ApiError(
      error.error || 'APIエラーが発生しました',
      response.status,
      error.details
    )
  }

  return data as T
}

/**
 * 症状キーワードで検索
 */
export async function searchSymptoms(
  keyword: string,
  petType: PetType
): Promise<SearchResponse> {
  return request<SearchResponse>('/api/search', {
    method: 'POST',
    body: JSON.stringify({ keyword, petType }),
  })
}

/**
 * ページ内容をAI要約
 */
export async function summarizePages(
  urls: string[],
  keyword: string,
  petType: PetType,
  aiConfig: AiConfig
): Promise<SummarizeResponse> {
  return request<SummarizeResponse>('/api/summarize', {
    method: 'POST',
    body: JSON.stringify({ urls, keyword, petType, aiConfig }),
  })
}

/**
 * PDF用HTMLを取得
 */
export async function generatePdf(
  summary: SymptomSummary,
  options?: PdfOptions
): Promise<PdfResponse> {
  return request<PdfResponse>('/api/pdf', {
    method: 'POST',
    body: JSON.stringify({
      summary,
      petInfo: options?.petInfo,
      userMemo: options?.userMemo,
    }),
  })
}
